import { HttpApiError, HttpApiGroup, HttpApiSchema } from "@effect/platform";
import { zodToEffectSchema } from "@redotech/effect-api-shared-common/zod";
import { FrontendProductSchema } from "@redotech/redo-agents-cards/support-agents/frontend-product";
import { OrderSchema } from "@redotech/redo-model/order-schema";
import { Schema } from "effect";
import * as z from "zod";
import { endpoint } from "../auth/endpoint";
import { DatabaseError } from "../auth/errors";
import { ConversationId } from "../ids/definitions";
import {
  WidgetConversationListItemEffectSchema,
  WidgetConversationResponseEffectSchema,
} from "../models/shopper-ai/conversation/widget-conversation";
import {
  associateWidgetConversationsWithCustomerPayloadSchema,
  convertWidgetConversationToSupportPayloadSchema,
  createWidgetConversationPayloadSchema,
  sendWidgetMessagePayloadSchema,
  widgetConciergeInteractedPayloadSchema,
  widgetMessageSendResponseSchema,
  widgetOkResponseSchema,
} from "../models/shopper-ai/conversation/widget-conversation-stream";
import {
  openStreamSessionResponseSchema,
  streamControlInputSchema,
  streamControlSuccessSchema,
} from "../models/stream-protocol";
import {
  ConfirmPendingToolCallInput,
  ConfirmPendingToolCallResponse,
} from "./chat-widget-confirm";

export class WidgetCapabilitiesResponse extends Schema.Class<WidgetCapabilitiesResponse>(
  "WidgetCapabilitiesResponse",
)({ supportV2: Schema.Boolean }) {}

export const ChatWidgetGroup = HttpApiGroup.make("chatWidget")
  .add(
    endpoint.widgetPublic
      .get("getCapabilities", "/widget/capabilities")
      .addSuccess(WidgetCapabilitiesResponse)
      .addError(HttpApiError.Unauthorized)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .get("getWidgetConversation", "/widget/conversations/:id")
      .setPath(Schema.Struct({ id: ConversationId }))
      .addSuccess(WidgetConversationResponseEffectSchema)
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .get("getWidgetConversationListItems", "/widget/conversations")
      .setUrlParams(
        Schema.Struct({ conversationIds: Schema.optional(Schema.String) }),
      )
      .addSuccess(Schema.Array(WidgetConversationListItemEffectSchema))
      .addError(HttpApiError.Unauthorized)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .post("openWidgetStream", "/widget/stream")
      .addSuccess(zodToEffectSchema(openStreamSessionResponseSchema))
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.ServiceUnavailable)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .get("connectWidgetStream", "/widget/stream/:sessionId")
      .setPath(Schema.Struct({ sessionId: Schema.String }))
      .addSuccess(
        Schema.String.pipe(
          HttpApiSchema.withEncoding({
            kind: "Text",
            contentType: "text/event-stream",
          }),
        ),
      )
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.ServiceUnavailable)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .post("controlWidgetStream", "/widget/stream/:sessionId")
      .setPath(Schema.Struct({ sessionId: Schema.String }))
      .setPayload(zodToEffectSchema(streamControlInputSchema))
      .addSuccess(zodToEffectSchema(streamControlSuccessSchema))
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.ServiceUnavailable)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .del("closeWidgetStream", "/widget/stream/:sessionId")
      .setPath(Schema.Struct({ sessionId: Schema.String }))
      .addSuccess(zodToEffectSchema(streamControlSuccessSchema))
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.ServiceUnavailable)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .post("createWidgetConversation", "/widget/conversations")
      .setPayload(zodToEffectSchema(createWidgetConversationPayloadSchema))
      .addSuccess(zodToEffectSchema(widgetMessageSendResponseSchema))
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .post("sendWidgetMessage", "/widget/conversations/:id/messages")
      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(zodToEffectSchema(sendWidgetMessagePayloadSchema))
      .addSuccess(zodToEffectSchema(widgetMessageSendResponseSchema))
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .post("notifyWidgetTyping", "/widget/conversations/:id/typing")
      .setPath(Schema.Struct({ id: ConversationId }))
      .addSuccess(zodToEffectSchema(widgetOkResponseSchema))
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.ServiceUnavailable)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .post(
        "convertWidgetConversationToSupport",
        "/widget/conversations/:id/convert-to-support",
      )
      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(
        zodToEffectSchema(convertWidgetConversationToSupportPayloadSchema),
      )
      .addSuccess(zodToEffectSchema(widgetMessageSendResponseSchema))
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetAuth
      .post(
        "associateWidgetConversationsWithCustomer",
        "/widget/conversations/associate-customer",
      )
      .setPayload(
        zodToEffectSchema(
          associateWidgetConversationsWithCustomerPayloadSchema,
        ),
      )
      .addSuccess(zodToEffectSchema(widgetOkResponseSchema))
      .addError(HttpApiError.Unauthorized)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .post("widgetConciergeInteracted", "/widget/concierge-interacted")
      .setPayload(zodToEffectSchema(widgetConciergeInteractedPayloadSchema))
      .addSuccess(zodToEffectSchema(widgetOkResponseSchema))
      .addError(HttpApiError.Unauthorized)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .post(
        "confirmPendingToolCall",
        "/widget/conversations/:id/confirm-tool-call",
      )
      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(ConfirmPendingToolCallInput)
      .addSuccess(ConfirmPendingToolCallResponse)
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .post("lookupProductsByIds", "/widget/conversations/:id/lookup-products")
      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(
        Schema.Struct({
          productIds: Schema.Array(Schema.String),
          includeOutOfStock: Schema.optional(Schema.Boolean),
        }),
      )
      .addSuccess(zodToEffectSchema(z.array(FrontendProductSchema)))
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .post("customerEmailEntry", "/widget/conversations/:id/customer-email")
      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(Schema.Struct({ email: Schema.String }))
      .addSuccess(Schema.Struct({ ok: Schema.Literal(true) }))
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .post(
        "uploadVisualizationImage",
        "/widget/conversations/:id/visualization-image",
      )
      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(
        Schema.Struct({
          toolCallId: Schema.String,
          imageData: Schema.String,
          contentType: Schema.String,
        }),
      )
      .addSuccess(
        Schema.Struct({
          imageUrl: Schema.String,
          imageHash: Schema.String,
          imageExtension: Schema.String,
        }),
      )
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .post("trackOrder", "/widget/conversations/:id/track-order")
      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(
        Schema.Struct({
          toolMessageId: Schema.String,
          orderEmail: Schema.String,
          orderId: Schema.String,
        }),
      )
      .addSuccess(
        Schema.Union(
          Schema.Struct({
            result: Schema.Literal("FOUND_TRACKABLE_FULFILLMENTS"),
          }),
          Schema.Struct({
            result: Schema.Literal("ERROR"),
            error: Schema.String,
          }),
        ),
      )
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .post("getOrderDetails", "/widget/conversations/:id/order-details")
      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(Schema.Struct({ orderId: Schema.String }))
      .addSuccess(
        zodToEffectSchema(z.object({ order: OrderSchema.optional() })),
      )
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(DatabaseError),
  )
  .add(
    endpoint.widgetPublic
      .post("addProductToCart", "/widget/conversations/:id/add-to-cart")
      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(
        Schema.Struct({
          wasSuccessful: Schema.Boolean,
          actionOriginMessageId: Schema.String,
          productTitle: Schema.optional(Schema.String),
          productId: Schema.String,
          variantId: Schema.String,
          quantity: Schema.Number,
          checkoutUrl: Schema.optional(Schema.String),
        }),
      )
      .addSuccess(
        Schema.Struct({ conversation: WidgetConversationResponseEffectSchema }),
      )
      .addError(HttpApiError.Unauthorized)
      .addError(HttpApiError.NotFound)
      .addError(DatabaseError),
  )
  .prefix("/api");
